import { Outlet, redirect, useLoaderData, type LoaderFunctionArgs } from "react-router";
import { Sidebar } from "@/components/layout/sidebar";
import { UserMenu } from "@/components/layout/user-menu";
import { currentUser, type UserProfile } from "@/services/auth";

/**
 * The application shell, and the authentication guard.
 *
 * The guard is this loader. It runs before anything inside renders, so no
 * authenticated page ever mounts without a session — the property the Next
 * middleware used to provide. It carries the path the analyst wanted, so
 * signing in lands them where they were headed rather than at a generic home
 * page.
 *
 * A guard in the browser is a routing convenience, not a security boundary.
 * The backend authorises every request on its own; nothing here is trusted by
 * it. Skipping this check gets you an empty page and a 401, not data.
 */
export async function workspaceLoader({ request }: LoaderFunctionArgs): Promise<UserProfile> {
  const user = await currentUser();
  if (user) return user;

  const { pathname, search } = new URL(request.url);
  const wanted = `${pathname}${search}`;
  throw redirect(wanted === "/" ? "/login" : `/login?next=${encodeURIComponent(wanted)}`);
}

export function WorkspaceLayout() {
  const user = useLoaderData<UserProfile>();

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-11 shrink-0 items-center justify-end border-b border-line bg-surface px-6">
          <UserMenu user={user} />
        </div>
        <main className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
